const storageKey = 'tp:recent-products:v1';
const lifetime = 30 * 24 * 60 * 60 * 1000;
const assetLoads = new Map();

function cleanHistory(value, now = Date.now()) {
  const seen = new Set();
  if (!Array.isArray(value)) return [];
  return value.filter(item => {
    if (!item || !/^\d+$/.test(String(item.id)) || typeof item.handle !== 'string' || !/^[a-z0-9][a-z0-9-]*$/.test(item.handle)) return false;
    if (!Number.isFinite(item.seenAt) || now - item.seenAt > lifetime || item.seenAt > now + 60000 || seen.has(String(item.id))) return false;
    seen.add(String(item.id));
    return true;
  }).slice(0, 20).map(({ id, handle, seenAt }) => ({ id: String(id), handle, seenAt }));
}

function readHistory() {
  try { return cleanHistory(JSON.parse(localStorage.getItem(storageKey) || '[]')); }
  catch { return []; }
}

function recordProduct(history, id, handle) {
  if (!/^\d+$/.test(id) || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(cleanHistory([{ id, handle, seenAt: Date.now() }, ...history.filter(item => item.id !== id)])));
  } catch {}
}

function loadAssets(template) {
  const tasks = [];
  for (const source of template.content.querySelectorAll('link[rel="stylesheet"], script[type="module"][src]')) {
    const address = source.getAttribute('src') || source.getAttribute('href');
    if (!assetLoads.has(address)) {
      assetLoads.set(address, new Promise((resolve, reject) => {
        const asset = source.cloneNode(true);
        asset.addEventListener('load', resolve, { once: true });
        asset.addEventListener('error', reject, { once: true });
        document.head.append(asset);
      }));
    }
    tasks.push(assetLoads.get(address));
  }
  return Promise.all(tasks);
}

class RecentlyViewed extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.onPageShow = event => { if (event.persisted) this.refresh(); };
    window.addEventListener('pageshow', this.onPageShow);
    this.refresh();
  }

  refresh() {
    this.controller?.abort();
    this.stopWaiting();
    this.controller = new AbortController();
    const controller = this.controller;
    const history = readHistory();
    const { currentId, currentHandle } = this.dataset;
    this.previous = history.filter(item => item.id !== currentId && item.handle !== currentHandle);
    recordProduct(history, currentId, currentHandle);
    if (!this.previous.length) {
      this.hidden = true;
      this.dataset.state = 'first-visit';
      return;
    }
    let loadStarted = false;
    const start = () => {
      if (loadStarted || !this.isConnected || controller.signal.aborted) return;
      loadStarted = true;
      this.stopWaiting();
      this.dataset.state = 'loading';
      this.load(controller).catch(() => { if (!controller.signal.aborted) this.dataset.state = 'unavailable'; });
    };
    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }
    let target = this;
    if (this.hidden) {
      this.dataset.state = 'waiting';
      this.trigger = document.createElement('span');
      this.trigger.setAttribute('data-recent-trigger', '');
      this.trigger.setAttribute('aria-hidden', 'true');
      this.trigger.style.cssText = 'display:block;flex:0 0 1px;width:1px;height:1px;margin-bottom:-1px;visibility:hidden;pointer-events:none';
      (this.closest('.shopify-section') || this).after(this.trigger);
      const parentStyle = getComputedStyle(this.trigger.parentElement);
      if (parentStyle.display.includes('flex') && parentStyle.flexDirection === 'column') {
        this.trigger.style.marginTop = `${-(parseFloat(parentStyle.rowGap) || 0)}px`;
      }
      target = this.trigger;
    }
    this.viewportObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) start();
    }, { rootMargin: '600px 0px' });
    this.viewportObserver.observe(target);
  }

  stopWaiting() {
    this.viewportObserver?.disconnect();
    this.viewportObserver = null;
    this.trigger?.remove();
    this.trigger = null;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.stopWaiting();
    window.removeEventListener('pageshow', this.onPageShow);
    this.started = false;
  }

  async fetchCard(item, signal) {
    try {
      const root = this.dataset.root.replace(/\/?$/, '/');
      const url = new URL(`${root}products/${encodeURIComponent(item.handle)}`, location.origin);
      url.searchParams.set('view', 'tp-recent-card');
      const response = await fetch(url, { signal, credentials: 'same-origin', priority: 'low' });
      if (!response.ok) return null;
      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      const card = parsed.querySelector('[data-recent-card]');
      if (!card || card.dataset.productId !== item.id || card.dataset.handle !== item.handle) return null;
      return card;
    } catch { return null; }
  }

  async load(controller) {
    const limit = Math.max(2, Math.min(8, Number(this.dataset.limit) || 8));
    const previous = this.previous.slice(0, limit);
    const results = new Array(previous.length);
    let nextIndex = 0;
    const worker = async () => {
      while (!controller.signal.aborted && nextIndex < previous.length) {
        const index = nextIndex++;
        results[index] = await this.fetchCard(previous[index], controller.signal);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, previous.length) }, worker));
    const cards = results.filter(Boolean);
    if (controller.signal.aborted) return;
    if (!this.isConnected || !cards.length) { this.dataset.state = 'empty'; return; }
    const fragment = this.querySelector('[data-recent-list]').content.cloneNode(true);
    const listing = fragment.querySelector('product-listing-component');
    const container = listing.querySelector('.embla__container');
    for (const card of cards) {
      const slide = document.createElement('div');
      slide.className = 'embla__slide';
      slide.append(document.importNode(card, true));
      container.append(slide);
    }
    const desktop = Number(listing.dataset.itemsPerRowDesktop);
    const mobile = Number(listing.dataset.itemsPerRowMobile);
    const options = JSON.parse(listing.dataset.options);
    options.breakpoints['(min-width: 992px)'].active = cards.length > desktop;
    options.breakpoints['(max-width: 991.98px)'].active = cards.length > mobile;
    listing.dataset.options = JSON.stringify(options);
    const embla = listing.querySelector('.embla');
    embla.dataset.hideNavigationDesktop = String(cards.length <= desktop);
    embla.dataset.hideNavigationMobile = String(cards.length <= mobile);
    embla.dataset.snapCount = String(Math.max(1, cards.length - desktop + 1));
    const wrapper = fragment.querySelector('.product-listing');
    wrapper.style.setProperty('--navigation-display-desktop', cards.length > desktop ? 'block' : 'none');
    wrapper.style.setProperty('--navigation-display-mb', cards.length > mobile ? 'block' : 'none');
    const footer = listing.querySelector('.embla__footer');
    footer?.classList.toggle('embla__footer--no-dk', cards.length <= desktop);
    footer?.classList.toggle('embla__footer--no-mb', cards.length <= mobile);
    await loadAssets(this.querySelector('[data-recent-assets]'));
    if (!this.isConnected || controller.signal.aborted) return;
    this.hidden = false;
    this.querySelector('[data-recent-mount]').replaceChildren(fragment);
    this.dataset.state = 'ready';
    this.dataset.count = String(cards.length);
  }
}

if (!customElements.get('tp-recently-viewed')) customElements.define('tp-recently-viewed', RecentlyViewed);
